import { Canvas, Container, PointerEvents } from '@tencent/alloy-renderer';
import { HistoryMarkComponent } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/history-mark-component';
import { CANVAS_ANIM_USE_CACHE } from '@src/slide/infrastructure/module/dependent/config/renderer/switch';
import SlideModel from '@src/slide/domain/ui-model/element-modeles/slide-model';
import { RenderPostionType } from '@src/js/constant';
import { CANVAS_WRAPPER } from '@src/slide/content-viewer/service/canvas-renderer/core/id-constants';
import { checkClickElement } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-animations/element-animation/anim-pointer-cursor';
import { InterfaceFactoryOptions } from './factory-options.interface';
import { IComponentWrapper } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/component-wrapper.interface';
import { isUiModelIsAttachment, isAudioElement, isVideoElement, isAttachmentModel } from '@src/slide/domain/ui-model/elements/element-judge';
import { updateWrapperTransform } from './utils/update-wrapper-transform';
import { IElementBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { IPictureModel } from '@src/slide/domain/ui-model/element-modeles/Interface/picture-model.interface';
import { getBrushCursorStyle } from '@src/slide/application/service/format-brush/core/util';
import { browser } from '@src/slide/infrastructure/module/independent/agent/common';
import { IContainerRawRenderData } from '@tencent/alloy-renderer/dist/raw-render-data';
import {
    getUploadingMediaAttachmentId,
} from '@src/slide/application/service/attachment-upload/attachment-manager';
import { EStatusEvents } from '@src/js/common/ark-extensions/dc-components/attachment-uploader/type';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { IEditorAppServices } from '@src/slide/application/entry/toc/editor/base/base-editor-app-service.interface';
import { IReactionDisposer, reaction } from 'mobx';

/**
 * 图形wrapper模块（shape、image、connector、group）
 * 包裹上述图形，提供以下能力
 * （1）元素的位移、旋转、翻转
 * （2）更新PointerEvents和光标展示
 * （3）修订记录变动痕迹
 * （4）画布尺寸调整触发
 * （5）公共事件：MouseDown、MouseUp（选中、拖拽入口）
 */
export class ComponentWrapper extends Container implements IComponentWrapper {
    // 生成图形的入参
    public readonly factoryOptions: InterfaceFactoryOptions<IElementBaseModel>;
    // 整个factory的更新，组合手动刷子元素时需要调用
    public readonly factoryUpdateComponent: () => void;
    // 整个factory的dispose，因为factory没有生命周期，所以需要依赖wrapper
    private readonly factoryDispose: () => void;
    // 修订记录查看时显示变动痕迹
    private historyMark: HistoryMarkComponent;
    // 画布调整时所需的对比记录
    private lastComponentRect: string;
    // 是否启用动画缓存
    private keepCacheForAnimation: boolean;
    // animBrushManager disposer
    private animBrushDisposer?: IReactionDisposer;

    // 获取当前 wrapper component实例（主要是为了解决component-anim-wrapper动态外包一层时获取wrapper麻烦）
    public get wrapperInstance(): IComponentWrapper {
        return this;
    }

    public constructor(
        factoryOptions: InterfaceFactoryOptions<IElementBaseModel>,
        factoryDispose: () => void,
        factoryUpdateComponent: () => void,
    ) {
        super();
        this.factoryOptions = factoryOptions;
        this.factoryDispose = factoryDispose;
        this.factoryUpdateComponent = factoryUpdateComponent;
        this.keepCacheForAnimation = false;
        this.updateComponent();
        this.id = `${CANVAS_WRAPPER}#${factoryOptions?.model?.key}`;
    }

    public dispose(): void {
        super.dispose();
        // cacel reaction
        this.animBrushDisposer?.();
        this.factoryDispose?.();
    }

    /**
     * factory层监听model变化，调用此函数刷新wrapper渲染
     */
    public updateComponent = (): void => {
        // 更新位移、旋转、翻转
        updateWrapperTransform(this, this.factoryOptions);
        // 更新整体 显隐、事件禁止、光标
        this.updateElementShowAndEventStatus();
        // 更新修订记录查看时的变更痕迹
        this.renderHistoryMark();
        // 画布尺寸调整触发
        this.checkWindowNeedResize();
        // 动画元素开启使用缓存
        this.useAnimCache();
        // 在外层更新光标
        this.observeAnimBrushManager();
    };

    // 更新图形整体的（1）显隐状态（2）PointerEvents（3）光标展示
    public updateElementShowAndEventStatus = (): void => {
        const {
            model: { selectable, hidden, isFromMaster, isFromLayout },
            isCroping, editingStateManager, isMasterEdit,
        } = this.factoryOptions;
        const insertState = editingStateManager?.insertState;
        // 悬浮窗选择窗格操作可以控制隐藏元素
        this.dealVisibleStatus();
        // 移动端 master layout 元素禁止事件
        const mobileMasterLayoutElement = browser.isMobile && !isMasterEdit && (isFromMaster || isFromLayout);
        // 以下情况禁掉所有事件（插入中 || 图片裁剪中 || 不可选 || 不可见 || 移动端 master layout 元素）
        this.pointerEvents = (insertState?.type || isCroping || !selectable || hidden || mobileMasterLayoutElement)
            ? PointerEvents.NONE : PointerEvents.AUTO;
        // 可编辑图形 显示可拖拽光标
        this.cursor = this.getCursor();
    };

    public dealVisibleStatus = (forceVisible?: boolean): void => {
        if (typeof forceVisible === 'boolean') {
            this.visible = forceVisible;
            return;
        }
        const { model: { hidden } } = this.factoryOptions;
        this.visible = !hidden && this.isShowCanvasAttachmentElement();
    };

    public generateRawRenderData(canvas: Canvas<unknown>): IContainerRawRenderData {
        const { model } = this.factoryOptions;
        // 不可见元素、附件 SSR不做渲染
        if (!this.visible || isAttachmentModel(model)) return { type: 'Container', children: [{ type: '' }] };
        return super.generateRawRenderData(canvas);
    }

    // 更新修订记录查看时的变更痕迹
    private renderHistoryMark = () => {
        if (browser.isMobile) {
            // 移动端没有修订记录
            return;
        }
        if (!this.historyMark) {
            this.historyMark = new HistoryMarkComponent(this.factoryOptions, this);
        }
        this.historyMark.renderComponent();
    };

    private observeAnimBrushManager() {
        if (this.animBrushDisposer) return;
        const animBrushManager = (AppServiceProvider.getInstance().getAppServices() as IEditorAppServices)
            .editableServiceLayer?.animBrushManager;
        if (!animBrushManager) return;

        this.animBrushDisposer = reaction(() => {
            const  animBrushState = (AppServiceProvider.getInstance().getAppServices() as IEditorAppServices)
                .editableServiceLayer?.animBrushManager.animBrushState;
            return [animBrushState];
        }, () => {
            this.cursor = this.getCursor();
        });
    }

    /**
     * 给动画元素开启缓存
     */
    private useAnimCache = (): void => {
        if (!CANVAS_ANIM_USE_CACHE) return;
        const { region, model: { id: modelId, parent: slideModel } = {} } = this.factoryOptions;
        // 编辑态的右侧编辑区域，以及播放态下的整页允许开启动画缓存
        const allowCacheRegion = [RenderPostionType.CANVAS,  RenderPostionType.PLAYER];
        if (!allowCacheRegion.includes(region)) return;

        // 当前Slide下的动画元素允许开启缓存
        const isSlideInstance = slideModel instanceof SlideModel;
        if (!isSlideInstance) return;

        // 通过当前slide， 获取所有动画的目标元素的id集合
        const allSeqs = slideModel?.getAllKindsOfSequence();
        if (!allSeqs) return;
        const animSpidSet = new Set();
        Object.keys(allSeqs).forEach((seqType) => {
            const seqTypeList = allSeqs[seqType];
            seqTypeList.forEach((seqList) => {
                seqList.forEach((actionSeq) => {
                    const { animList = [] } = actionSeq;
                    animList.forEach((animModel) => {
                        animSpidSet.add(animModel?.spid);
                    });
                });
            });
        });
        // 元素有动画则开启缓存，否则关闭
        this.keepCacheForAnimation = animSpidSet.has(modelId);
    };

    // 光标样式
    private getCursor = () => {
        const { isEditable, region, model } = this.factoryOptions;
        const { uniqueID } = model || {};
        // todo logic改版后 此分支拆分到各logic种
        if (RenderPostionType.PLAYER === region) {
            if (checkClickElement(this.factoryOptions)) {
                return 'pointer';
            }
        } else {
            const formatBrushState = this.factoryOptions.formatBrushManager?.formatBrushState;
            /** NOTE:
             * 此处使用 AppServiceProvider 中 animBrushManager，
             * 因为首屏渲染后 factoryOptions 中 animBrushManager 未能触发组件更新或更新后拿不到 animBrushManager 值，导致光标样式不更新
             * 存在问题：远程演示态 AppServiceProvider.getInstance().getAppServices() 可能拿不到 animBrushManager，所以一路使用 ?. 可选取值
             * 但是演示态也不需要更新光标样式
             */
            const animBrushState = (AppServiceProvider.getInstance().getAppServices() as IEditorAppServices)
                .editableServiceLayer?.animBrushManager?.animBrushState;
            if (animBrushState) {
                // Office 中动画刷动画来源元素或者动画刷到动画不支持绑定鼠标指针不变
                const isOriginElement = uniqueID === animBrushState?.originElId;
                const isAllMediaEffect = !this.isMediaElement(model) && animBrushState.allMediaEffect;
                if (isOriginElement || isAllMediaEffect) {
                    return 'auto';
                }
                return getBrushCursorStyle(false);
            } if (formatBrushState?.data) {
                return getBrushCursorStyle(formatBrushState?.data === 'text');
            } if (isEditable) {
                return 'move';
            }
        }
        return 'auto';
    };

    private isMediaElement(model) {
        return model?.video || model?.audio;
    }


    // 当图形的宽高||坐标变动时，需要触发画布尺寸的调整（否则拖拽图形出画布时，协同者画布不会调整）
    private checkWindowNeedResize = () => {
        const { width, height, left, top } = this.factoryOptions.model?.props?.style || {};
        const newComponentRect = [width, height, left, top].join('|');
        if (this.lastComponentRect !== newComponentRect) {
            if (this.lastComponentRect) {
                window.dispatchEvent(new Event('PPT-resize'));
            }
            this.lastComponentRect = newComponentRect;
        }
    };

    /**
     * 是否需要显示 canvas 版本的附件元素（某些场景下附件元素会使用 dom 渲染，所以可能不需要显示 canvas 版本的附件元素）
     * @returns
     */
    // eslint-disable-next-line complexity
    private isShowCanvasAttachmentElement = (): boolean => {
        const { model, region } = this.factoryOptions;

        if (isUiModelIsAttachment(model)) {
            return !model.isGhostAttachment(region);
        }
        return true;
    };
}
